import { Link, useNavigate } from "@tanstack/react-router";
import { FORMA_PAGO, entradasOrdenadas, pagoCreateSchema } from "@uirtus/shared";
import { useState, type FormEvent } from "react";
import { AplicacionPagos, type AplicacionFactura } from "../componentes/aplicacion-pagos";
import { SelectorCliente, type ClienteResumen } from "../componentes/selector-cliente";
import { Aviso, Boton, Campo, Entrada, Seleccion } from "../componentes/ui";
import { useCrearPago } from "../lib/pagos";

/** "2026-08-10T14:30" (datetime-local) → "2026-08-10T14:30:00" que pide el esquema. */
const conSegundos = (v: string): string => (v.length === 16 ? `${v}:00` : v);

export function PagoNuevo() {
  const navegar = useNavigate();
  const crear = useCrearPago();

  const [cliente, setCliente] = useState<ClienteResumen | null>(null);
  const [fechaPago, setFechaPago] = useState(() => {
    const ahora = new Date();
    ahora.setMinutes(ahora.getMinutes() - ahora.getTimezoneOffset());
    return ahora.toISOString().slice(0, 16);
  });
  const [formaPago, setFormaPago] = useState("03");
  const [numOperacion, setNumOperacion] = useState("");
  const [aplicaciones, setAplicaciones] = useState<AplicacionFactura[]>([]);
  const [errores, setErrores] = useState<Record<string, string>>({});

  const total = aplicaciones
    .reduce((acc, a) => acc + (Number(a.impPagado) || 0), 0)
    .toFixed(2);

  function enviar(e: FormEvent) {
    e.preventDefault();

    const datos = {
      clienteId: cliente?.id,
      fechaPago: conSegundos(fechaPago),
      formaPago,
      moneda: "MXN",
      monto: total,
      numOperacion: numOperacion.trim() || undefined,
      documentos: aplicaciones.map((a) => ({
        facturaId: a.facturaId,
        impPagado: a.impPagado,
      })),
    };

    // Mismo esquema que valida el backend: si pasa aquí, pasa allá.
    const resultado = pagoCreateSchema.safeParse(datos);
    if (!resultado.success) {
      const porCampo: Record<string, string> = {};
      for (const issue of resultado.error.issues) {
        const campo = String(issue.path[0] ?? "");
        if (campo && !porCampo[campo]) porCampo[campo] = issue.message;
      }
      setErrores(porCampo);
      return;
    }

    crear.mutate(datos as never, {
      onSuccess: (pago) => void navegar({ to: "/pagos/$id", params: { id: pago.id } }),
    });
  }

  return (
    <div className="space-y-4">
      <Link to="/pagos" className="text-sm text-marca-600 hover:underline">
        ← Pagos
      </Link>
      <h1 className="text-lg font-semibold">Registrar pago</h1>

      <form
        onSubmit={enviar}
        className="space-y-5 rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
      >
        {crear.error && <Aviso>{crear.error.message}</Aviso>}

        <SelectorCliente
          valor={cliente}
          alSeleccionar={(c) => {
            setCliente(c);
            setAplicaciones([]);
          }}
          error={errores["clienteId"]}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Fecha de pago" requerido error={errores["fechaPago"]}>
            <Entrada
              type="datetime-local"
              value={fechaPago}
              onChange={(e) => setFechaPago(e.target.value)}
              error={!!errores["fechaPago"]}
            />
          </Campo>
          <Campo etiqueta="Forma de pago" requerido error={errores["formaPago"]}>
            <Seleccion value={formaPago} onChange={(e) => setFormaPago(e.target.value)}>
              {entradasOrdenadas(FORMA_PAGO)
                .filter(([clave]) => clave !== "99")
                .map(([clave, nombre]) => (
                  <option key={clave} value={clave}>
                    {clave} · {nombre}
                  </option>
                ))}
            </Seleccion>
          </Campo>
        </div>

        <Campo
          etiqueta="Número de operación"
          error={errores["numOperacion"]}
          ayuda="Opcional: referencia de la transferencia o el depósito"
        >
          <Entrada value={numOperacion} onChange={(e) => setNumOperacion(e.target.value)} />
        </Campo>

        <fieldset className="space-y-3 border-t border-slate-200 pt-4 dark:border-slate-800">
          <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Facturas a las que se aplica
          </legend>
          <AplicacionPagos
            clienteId={cliente?.id}
            value={aplicaciones}
            onChange={setAplicaciones}
          />
          {errores["documentos"] && <Aviso>{errores["documentos"]}</Aviso>}
        </fieldset>

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
          <Boton
            type="button"
            variante="secundario"
            onClick={() => void navegar({ to: "/pagos" })}
          >
            Cancelar
          </Boton>
          <Boton type="submit" disabled={crear.isPending}>
            {crear.isPending ? "Guardando…" : "Registrar pago"}
          </Boton>
        </div>
      </form>
    </div>
  );
}
